import { useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Box, Button } from '@machobear/design-system'
import { colors, breakpoints, zIndex } from '@/styles'
import { toUnit } from '@/utils/styles'
import { CssUnit } from '@/types'
import { isAppWebView } from '@/hooks/use-app-webview'

const Wrapper = styled.div<{ height: CssUnit; sticky: boolean }>`
  height: ${props => toUnit(props.height)};
  background-color: ${colors.white};
  display: flex;
  align-items: center;
  border-top: 1px solid ${colors.grey88};
  z-index: ${zIndex.buttonBar};

  ${props =>
    props.sticky &&
    `
    position: sticky;
    left: 0;
    right: 0;
    bottom: 0;
  `}

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-left: unset;
  }

  @media print {
    display: none;
    visibility: hidden;
  }
`

const InnerWrapper = styled.div<{ height: CssUnit }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: ${props => toUnit(props.height)};
  max-width: ${breakpoints.dashboardMobile};
  padding: 12px 24px;
  margin-right: auto;
  margin-left: auto;
  align-items: center;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 12px 16px;
  }
`

type ButtonBarProps = {
  children?: React.ReactNode
  height?: CssUnit
  sticky?: boolean
}

export const ButtonBar: React.FC<ButtonBarProps> = ({
  children,
  height = 64,
  sticky = false,
  ...rest
}) => {
  useEffect(() => {
    const root = document.documentElement
    const value = height ? `${toUnit(height)}` : '0px'
    root.style.setProperty('--toaster-bottom', value)
    return () => {
      const current = root.style.getPropertyValue('--toaster-bottom')
      if (!current || current !== value) return
      root.style.removeProperty('--toaster-bottom')
    }
  }, [height])
  return (
    <Wrapper height={height} sticky={sticky} {...rest}>
      <InnerWrapper height={height}>{children}</InnerWrapper>
    </Wrapper>
  )
}

type DefaultButtonBarProps = ButtonBarProps & {
  handleBack?: () => void
  isSubmitting?: boolean
  hideBackButton?: boolean
}

export const DefaultButtonBar: React.FC<DefaultButtonBarProps> = ({
  handleBack,
  isSubmitting,
  children,
  sticky = true,
  hideBackButton = false,
  ...rest
}) => {
  const { t } = useTranslation()
  const history = useHistory()

  const onHandleBack = () => (handleBack ? handleBack() : history.goBack())

  const isApp = isAppWebView(history)
  if (isApp) return null
  return (
    <ButtonBar {...rest} sticky>
      {!hideBackButton && (
        <Box>
          <Button variant="ghost" size="small" disabled={isSubmitting} onClick={onHandleBack}>
            {t('common.back', 'Back')}
          </Button>
        </Box>
      )}
      {children}
    </ButtonBar>
  )
}

export default ButtonBar
